import React from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useMount, useUnmount } from 'react-use';
import { getWindow, trackNavigatedToPage } from '@surfline/web-common';

import { setMapLocation, getUserLocation, SET_MAP_LOCATION } from 'actions/map';
import Map from 'containers/Map';
import { getOrCreateStore, type ReduxInitialProps } from 'stores';
import { useAppDispatch } from 'stores/hooks';
import { stripAtCharacterFromLocation } from 'utils/urls/mapPaths';

const SYNC_URL = true;

/**
 * Parses a location string in the format "lat,lon,zoomz" and returns the coordinates
 * @param locationString The location string to parse
 * @returns The parsed coordinates or null if parsing fails
 */
const parseLocationString = (locationString: string) => {
  try {
    const cleanLocation = stripAtCharacterFromLocation(locationString);
    const pathLocation = cleanLocation.split(',');

    if (pathLocation && pathLocation.length >= 3) {
      const lat = Number(pathLocation[0]);
      const lon = Number(pathLocation[1]);
      const zoom = Number(pathLocation[2].replace('z', ''));

      // Only return if we have valid numbers
      if (!Number.isNaN(lat) && !Number.isNaN(lon) && !Number.isNaN(zoom)) {
        return {
          center: { lat, lon },
          zoom,
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to parse location:', error);
    return null;
  }
};

const SpotMapPageWithLocation: NextPage<{}, ReduxInitialProps> = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  // When the map page mounts with the location in the URL, we want to sync it to the store and
  // session storage.
  useMount(async () => {
    const win = getWindow();
    if (win) {
      win.document.body.classList.add('sl-hide-ze-widget', 'sl-map-page');

      trackNavigatedToPage('Map', {
        url: `${win?.location?.href ?? ''}`,
        path: router?.asPath,
        name: 'Map',
      });

      // Get location from URL query parameter
      const { location } = router.query as { location: string };

      // Always prioritize URL coordinates over everything else
      if (location) {
        const parsedLocation = parseLocationString(location);
        if (parsedLocation) {
          // Clear session storage to prevent it from overriding URL parameters
          try {
            win.sessionStorage.removeItem('slMapLocation');
          } catch (e) {
            console.error('Failed to clear session storage:', e);
          }

          // Dispatch with URL sync to ensure coordinates are in URL
          dispatch(setMapLocation(parsedLocation, router, SYNC_URL));
          return;
        }
      }

      // Get the current state to check if we already have valid coordinates from SSR
      const store = getOrCreateStore();
      const currentState = store.getState();
      const currentLocation = currentState?.map?.location;

      // Check if we already have valid coordinates from server-side rendering
      const hasValidCoordinates =
        currentLocation &&
        currentLocation.center &&
        'lat' in currentLocation.center &&
        'lon' in currentLocation.center &&
        typeof currentLocation.center.lat === 'number' &&
        typeof currentLocation.center.lon === 'number' &&
        typeof currentLocation.zoom === 'number';

      if (hasValidCoordinates) {
        // No need to do anything, the map will use the coordinates from the store
        return;
      }

      // Fallback to session storage only if URL parsing failed and no SSR coordinates
      const storageItem = win.sessionStorage.getItem('slMapLocation');
      if (storageItem) {
        try {
          const sessionLocation = JSON.parse(storageItem);
          dispatch(setMapLocation(sessionLocation, router, SYNC_URL));
          return;
        } catch (e) {
          console.error('Failed to parse session location:', e);
        }
      }

      // If both URL and session storage fail, use GPS location
      try {
        const userLocation = await getUserLocation();
        if (userLocation && userLocation.center) {
          dispatch(setMapLocation(userLocation, router, SYNC_URL));
        }
      } catch (error) {
        console.error('Failed to get user location:', error);
      }
    }
  });

  useUnmount(() => {
    const win = getWindow();
    win.document.body.classList.remove('sl-hide-ze-widget', 'sl-map-page');
  });

  return <Map />;
};

SpotMapPageWithLocation.getInitialProps = async (context) => {
  const store = getOrCreateStore();
  const isServer = !!context.req;

  // Common props for all return scenarios
  const baseProps = {
    isAppBannerEnabled: true,
    isFooterVisible: false,
  };

  // For client-side, make sure we return a type compatible with ReduxInitialProps
  if (!isServer) {
    return baseProps as ReduxInitialProps;
  }

  // Server-side specific logic
  const { location } = context.query as { location: string };

  if (location) {
    const parsedLocation = parseLocationString(location);

    if (parsedLocation) {
      // Directly dispatch to the store to update the initial state
      store.dispatch({
        type: SET_MAP_LOCATION,
        center: parsedLocation.center,
        zoom: parsedLocation.zoom,
      });
    }

    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      location,
    });
  }

  // For all server requests, include the Redux state
  return {
    ...baseProps,
    ssrReduxState: store.getState(),
  } as ReduxInitialProps;
};

export default SpotMapPageWithLocation;
